/**
 * <TutorSettingsGeneralPage> — « Général », la première page des paramètres du masteur (page).
 *
 * L'onglet de l'écran partagé d'aujourd'hui, tel qu'un tuteur le voit : son prénom, son nom, son
 * téléphone et son adresse email (verrouillée : seul un admin la change), la note qui renvoie au
 * support pour changer d'email, et l'entrée vers le mot de passe. Pas d'abonnement ni de parent :
 * ce sont des branches de l'élève (`<StudentSettingsGeneralPage>`).
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { TextInput } from "../../atoms/TextInput"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { SettingsSection } from "../../molecules/SettingsSection"
import {
    AccountFormCardSkeleton,
    AccountSettingsTemplate,
    type AccountSettingsShellProps,
} from "../../templates/AccountSettingsTemplate"
import { type UITutorGeneralSettings } from "../../../ports/primary/ui/types/tutorCompte"

export type TutorGeneralSettingsField = keyof UITutorGeneralSettings

export type TutorSettingsGeneralPageProps = AccountSettingsShellProps & {
    /** `null` tant que le compte n'est pas là. */
    values: UITutorGeneralSettings | null
    errors?: Partial<Record<TutorGeneralSettingsField, string>>
    onChange: (field: TutorGeneralSettingsField, value: string) => void
    onContactSupport: () => void
    onChangePassword: () => void
}

export function TutorSettingsGeneralPage(props: TutorSettingsGeneralPageProps) {
    const { values, errors = {}, onChange, onContactSupport, onChangePassword, ...shell } = props

    const busy = shell.submitting === true
    const field = (name: TutorGeneralSettingsField) => ({
        value: values?.[name] ?? "",
        onChangeText: (text: string) => onChange(name, text),
        error: errors[name],
    })

    return (
        <AccountSettingsTemplate {...shell} title="Général" skeleton={<AccountFormCardSkeleton fields={4} />}>
            {values != null && (
                <Stack gap="4">
                    <Card p="4">
                        <Stack gap="3">
                            <TextInput {...field("firstname")} label="Prénom" placeholder="Prénom" disabled={busy} />
                            <TextInput {...field("lastname")} label="Nom" placeholder="Nom" disabled={busy} />
                            <TextInput
                                {...field("mobile")}
                                label="Numéro de téléphone"
                                placeholder="ex : 0601020304"
                                keyboardType="phone-pad"
                                textContentType="telephoneNumber"
                                disabled={busy}
                            />
                            <TextInput
                                {...field("email")}
                                label="Adresse email"
                                placeholder="Adresse email"
                                textContentType="emailAddress"
                                disabled
                            />
                        </Stack>
                    </Card>

                    <Row gap="1" align="center" justify="center" style={styles.note}>
                        <Text size="text-md" color="text-muted" center>
                            Si tu souhaites modifier une adresse email,
                        </Text>
                        <TextButton label="contacte le support" size="sm" onPress={onContactSupport} />
                    </Row>

                    <SettingsSection
                        rows={[
                            {
                                key: "password",
                                iconName: "lock",
                                label: "Modifier mon mot de passe",
                                onPress: onChangePassword,
                            },
                        ]}
                    />
                </Stack>
            )}
        </AccountSettingsTemplate>
    )
}

const styles = StyleSheet.create(() => ({
    note: { flexWrap: "wrap" },
}))
